import { useCallback, useEffect, useState } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import equal from 'fast-deep-equal';
import { v4 as uuidv4 } from 'uuid';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { TitleWithAddButton } from 'UIPackage/MainModule/components/TitleWithAddButton';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { IDateWithPeriod } from 'APIPackage/AdminModule/interfaces/IDateWithPeriod';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { compareDates, formatDate, formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './AdminCatalogsExports.module.scss';
import { adminCatalogsExportsSubmit, adminGetFetchSettings } from '../actions/adminAction';
import { getAdminFetchSettings } from '../selectors/adminSelectors';
import { IExportForm } from '../interfaces/IExportForm';

interface IFormDatePeriod extends IExportForm {
  id?: number;
  deleteId?: number;
}
interface IAdminCatalogsExportsStoreProps {
  settings: IAdminSetting[];
}

const AdminCatalogsExportsWithoutStore = ({ settings }: IAdminCatalogsExportsStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    dispatch(adminGetFetchSettings());
  }, [dispatch]);

  const [initialValues, setInitialValues] = useState<IExportForm | null>(null);
  const [isErrorOutputPeriodDateForm, setIsErrorOutputDatePeriodForm] = useState(false);
  const [isErrorExportPeriodDateForm, setIsErrorExportPeriodDateForm] = useState(false);

  useEffect(() => {
    setInitialValues(
      settings.reduce((values: Partial<IExportForm>, setting): IExportForm => {
        if (setting.periods) {
          return {
            ...values,
            [`${setting.code}_PERIOD`]: setting.periods.map(item => {
              return {
                id: uuidv4(),
                startDate: formatDateLocale({
                  date: item.startDate || '',
                  dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                  parseFormat: DATE_FORMAT_ISO_FNS,
                }),
                endDate: formatDateLocale({
                  date: item.endDate || '',
                  dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                  parseFormat: DATE_FORMAT_ISO_FNS,
                }),
              };
            }),
          };
        }

        return {
          ...values,
          [setting.code]: formatDate({ date: setting.value || '', dateFormat: DATE_FORMAT_LONG_FNS }),
        };
      }, {}),
    );
  }, [settings]);

  const handleFormSubmit = useCallback(
    (data: IExportForm) => {
      dispatch(adminCatalogsExportsSubmit(data, settings));
    },
    [dispatch, settings],
  );

  function renderForm({ handleSubmit, form }: FormRenderProps<IExportForm>) {
    const { push, update } = form.mutators;
    const addPeriodDate = (field: keyof IExportForm) => {
      push(field, {
        id: uuidv4(),
      });
    };

    const makeRemoveDateFromPeriod = (field: keyof IExportForm, index: number, data: IFormDatePeriod) => () => {
      const { id } = data;
      const deleteId = id;

      update(field, index, {
        ...data,
        deleteId,
      });
    };

    const validateDatePicker = (value: IDateWithPeriod, startDate?: boolean) => {
      const validator = (fromDate: string | undefined, toDate: string | undefined, start?: boolean) => {
        if (
          !formatDateLocale({
            date: fromDate || '',
            dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
            parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
          }) ||
          !formatDateLocale({
            date: toDate || '',
            dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
            parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
          })
        ) {
          return 'Некорректный период';
        }
        const resultCompare = compareDates({
          dateLeft: fromDate || '',
          dateRight: toDate || '',
          parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
        });

        if (start && toDate && fromDate && resultCompare === 1) {
          return 'Дата начала периода должна быть меньше даты окончания';
        }
        if (fromDate && toDate && resultCompare === 1) {
          return 'Дата окончания периода должна быть больше даты начала';
        }
        if ((toDate && !fromDate && start) || (fromDate && !toDate && !start)) {
          return 'Поле не может быть пустым';
        }
        if (!toDate && !fromDate) {
          return 'Поле не может быть пустым';
        }
      };

      return validator(value.startDate, value.endDate, startDate);
    };

    function onAddPeriodDateClick() {
      addPeriodDate('KNM_PLAN_MATCH_OUTPUT_DATE_PERIOD');
    }

    function onAddPeriodDate() {
      addPeriodDate('KNM_MATCH_EXPORT_DATE_PERIOD');
    }

    return (
      <form className={styles.Form} onSubmit={handleSubmit}>
        <Row>
          <FieldBlock label="Выводить совпадения по КНМ и по плану" placementLabel="left" color="black">
            <DatePickerField name="KNM_PLAN_MATCH_OUTPUT_DATE" />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Экспорт списка разрешения совпадения" placementLabel="left" color="black">
            <DatePickerField name="KNM_MATCH_EXPORT_DATE" />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Экспорт комплекта документов для КНО" placementLabel="left" color="black">
            <DatePickerField name="EXPORT_DOCUMENTS_KNO_DATE" />
          </FieldBlock>
        </Row>
        <Row margin="medium">
          <FieldBlock label="Загрузка планов" placementLabel="left" color="black">
            <DatePickerField name="IMPORT_PLANS_DATE" />
          </FieldBlock>
        </Row>
        <Row>
          <TitleWithAddButton text="Ограничение отображения перечня совпадающих КНМ" onClick={onAddPeriodDateClick} />
        </Row>
        <FieldArray name="KNM_PLAN_MATCH_OUTPUT_DATE_PERIOD" isEqual={equal}>
          {({ fields }) => (
            <Row margin="medium">
              {fields.map((periodItem, index) => {
                const { id, deleteId } = fields.value[index];

                if (deleteId) {
                  return null;
                }
                const fieldId = id;
                const uniqueIndex =
                  fields.value.filter(item => !item.deleteId).findIndex(item => item.id === fieldId) + 1;
                const resultValidate = (start?: boolean) => validateDatePicker(fields.value[index], start);
                const resultValidateStarDate = resultValidate(true);
                const resultValidateEndDate = resultValidate();

                if (!resultValidateStarDate && !resultValidateEndDate) {
                  setIsErrorOutputDatePeriodForm(false);
                } else {
                  setIsErrorOutputDatePeriodForm(true);
                }

                return (
                  <li className={styles.Period} key={uniqueIndex}>
                    {`${uniqueIndex}.`}
                    <DatePickerField
                      className={styles.FieldPeriod}
                      timeFormat
                      name={`${periodItem}.startDate`}
                      invalid={!!resultValidateStarDate}
                      error={resultValidateStarDate}
                      classNameError={styles.Error}
                    />
                    —
                    <DatePickerField
                      className={styles.FieldPeriod}
                      timeFormat
                      name={`${periodItem}.endDate`}
                      invalid={!!resultValidateEndDate}
                      error={resultValidateEndDate}
                      classNameError={styles.Error}
                    />
                    <CloseButton
                      className={styles.QuestionDeleteButton}
                      onClick={makeRemoveDateFromPeriod(
                        'KNM_PLAN_MATCH_OUTPUT_DATE_PERIOD',
                        index,
                        fields.value[index],
                      )}
                    />
                  </li>
                );
              })}
            </Row>
          )}
        </FieldArray>
        <Row>
          <TitleWithAddButton text="Ограничение экспорта перечня совпадающих КНМ" onClick={onAddPeriodDate} />
        </Row>
        <FieldArray name="KNM_MATCH_EXPORT_DATE_PERIOD" isEqual={equal}>
          {({ fields }) => (
            <Row margin="medium">
              {fields.map((periodItem, index) => {
                const { id, deleteId } = fields.value[index];

                if (deleteId) {
                  return null;
                }

                const fieldId = id;
                const uniqueIndex =
                  fields.value.filter(item => !item.deleteId).findIndex(item => item.id === fieldId) + 1;
                const resultValidate = (start?: boolean) => validateDatePicker(fields.value[index], start);
                const resultValidateStarDate = resultValidate(true);
                const resultValidateEndDate = resultValidate();

                if (!resultValidateStarDate && !resultValidateEndDate) {
                  setIsErrorExportPeriodDateForm(false);
                } else {
                  setIsErrorExportPeriodDateForm(true);
                }

                return (
                  <li className={styles.Period} key={uniqueIndex}>
                    {`${uniqueIndex}.`}
                    <DatePickerField
                      className={styles.FieldPeriod}
                      name={`${periodItem}.startDate`}
                      timeFormat
                      invalid={!!resultValidateStarDate}
                      error={resultValidateStarDate}
                      classNameError={styles.Error}
                    />
                    —
                    <DatePickerField
                      className={styles.FieldPeriod}
                      name={`${periodItem}.endDate`}
                      timeFormat
                      invalid={!!resultValidateEndDate}
                      error={resultValidateEndDate}
                      classNameError={styles.Error}
                    />
                    <CloseButton
                      className={styles.QuestionDeleteButton}
                      onClick={makeRemoveDateFromPeriod('KNM_MATCH_EXPORT_DATE_PERIOD', index, fields.value[index])}
                    />
                  </li>
                );
              })}
            </Row>
          )}
        </FieldArray>
        <Button
          color="primary"
          type="submit"
          variant="contained"
          text="Применить"
          disabled={isErrorOutputPeriodDateForm || isErrorExportPeriodDateForm}
        />
      </form>
    );
  }

  return (
    <div className={styles.Wrapper}>
      <div className="shared-section-title">Управление расписанием</div>
      <Form
        onSubmit={handleFormSubmit}
        render={renderForm}
        initialValues={initialValues}
        mutators={{ ...arrayMutators }}
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IAdminCatalogsExportsStoreProps => ({
  settings: getAdminFetchSettings(state),
});

export const AdminCatalogsExports = connect(mapStateToProps)(AdminCatalogsExportsWithoutStore);
